import { forwardRef, useId, type CSSProperties, type ReactNode, type Ref } from 'react';
import { IconMedallion } from '../../atoms/icon-medallion/icon-medallion.js';
import { VisuallyHidden } from '../../atoms/visually-hidden/visually-hidden.js';

/**
 * ChoiceCard — one selectable option drawn as a card: an optional medallion, the
 * option's name, the one-line consequence of choosing it, an optional trailing
 * tag, and a corner check when it is chosen.
 *
 * Corpus: `SI:107-116` and `:269-282` are the stacked role card —
 * a column at `gap: 7px`, `text-align: start`, an 18px radius, `18px 18px 16px`
 * of padding, a 1.5px border that turns green when chosen, a 15.5px/700 name
 * over a 12.5px/1.45 description, and a 20px round check absolutely positioned at
 * `top: 12px; inset-inline-end: 12px`. `DB:1395-1399` and
 * `:2139-2147` are the row form: one line, a `min-width: 0; flex: 1` label and a
 * `nowrap` tag, dashed while the thing is not yet supplied and solid green once
 * it is. `:2092-2102` and `:2123-2131` are the compact form at a 12px radius.
 * `:100-105` is the choice with its consequence line beneath.
 * `PR1:117-124` is the entry card: a whole-card anchor, a
 * 24px radius on `--rp-surface-tile`, a tag, a 22px title, a flexing body and a
 * coloured call to action, lifting on hover.
 *
 * **Three corpus defects closed rather than copied.**
 *
 * 1. `SI:109` is a `<button>` with no `aria-pressed` at all —
 *    the state is a border, a fill and a ✓ glyph, none of which a screen-reader
 *    user receives. Every card carries `aria-pressed` here, so the state is
 *    programmatic in both the exclusive sets (`DB:2094`,
 *    `:2123-2131`) and the multi-select ones.
 *
 *    **`role="radio"` was built and then removed, which is worth recording.** It
 *    announced exclusivity correctly and delivered none of it: a radio group is
 *    one tab stop with arrow-key selection and roving focus, and a card cannot
 *    own any of that when the group is the caller's. The result announced
 *    itself a radio to a keyboard user and then ignored every key a radio
 *    answers, which is worse than the honest toggle — `aria-pressed` under-states
 *    the semantics, but nothing it promises is missing. A real radio group needs
 *    a component that owns the run; that is an organism, and it is filed rather
 *    than half-built here.
 * 2. `DB:103` renders the consequence as a sibling span. It is
 *    bound with `aria-describedby` here, because choosing without hearing the
 *    consequence is the failure the pattern exists to prevent.
 * 3. The ✓ badge and the medallion glyph are decoration and are `aria-hidden`;
 *    `selectedStateLabel` or the `tag` carries the state in words.
 *
 * **The consequence line is a product rule, not decoration.** Every decision in
 * the corpus carries a plain-language note of what choosing it does
 * (`DB:103`). A card with a consequence available and none
 * rendered is a defect.
 *
 * Two behaviours stay with the caller and are named so nobody looks for them
 * here: choosing a category resets its sub-option and its evidence
 * (`DB:2129`), and choosing a role recomputes which address roles
 * apply (`SI:269-272`). The card reports the choice; the caller
 * owns the consequence.
 *
 * `DB:2099` and `:2144` branch on `dir === 'rtl'` to pick left or
 * right. Both are replaced by `text-align: start`, which needs no branch.
 *
 * The pill `Button` atom is deliberately not composed: this control is a card,
 * and wearing a pill's skin to inherit its element would fight every rule in
 * `choice-card.css`. The medallion is `IconMedallion`, which is the corpus's own
 * `iconWrap` (`SI:284`).
 *
 * Every string a person reads arrives from the caller.
 */
interface ChoiceCardCore {
  /** The option's name. Copy. */
  readonly label: string;
  /** The consequence of choosing it, bound as the card's description. Copy. */
  readonly description?: string | undefined;
  /** Trailing status word — the "added ✓" / "add" pair at `:2142`. Copy. */
  readonly tag?: string | undefined;
  /** Medallion glyph, always `aria-hidden`. */
  readonly icon?: ReactNode | undefined;
  readonly selected?: boolean | undefined;
  readonly onSelect?: (() => void) | undefined;
  /** The entry variant's call-to-action line. Copy. */
  readonly ctaLabel?: string | undefined;
  /**
   * The CTA's accent (`PR1:123` binds `card.color` per card). A closed set of
   * role names rather than a colour: an open string let a caller put any value
   * behind the CTA's `color`, so `#ffffff` on the entry tile's own grey was
   * representable and every contrast test stayed green, because none of them can
   * see a value the caller supplies at runtime. These five map to the
   * `--rp-role-*` tokens, which `contrast.test.ts` already holds to 4.5:1.
   */
  readonly accent?: 'tenant' | 'advocate' | 'manager' | 'owner' | 'tradie' | undefined;
  readonly variant?: 'stacked' | 'row' | 'compact' | 'entry' | undefined;
  /** The corpus's "not yet real" edge (`:2144`): dashed means not yet supplied. */
  readonly dashed?: boolean | undefined;
  /** The word for the chosen state. Required where no `tag` carries it. Copy. */
  readonly selectedStateLabel?: string | undefined;
}

/**
 * A card either navigates or it is a control that can be switched off, and the
 * type is what enforces the choice.
 *
 * `disabled` alongside `href` was representable and could not be honoured. The
 * `disabled` attribute does not apply to an anchor; `aria-disabled` announces
 * without stopping anything; and refusing the `click` still leaves "open link in
 * a new tab", middle-click and every other path the browser follows straight
 * from `href` without React seeing an event. A card presented as unavailable
 * that opens on a context menu is worse than one that is plainly a link, so a
 * caller with nothing to link to renders no link.
 */
type ChoiceCardTarget =
  | {
      /** The entry variant: the whole card becomes one anchor, one tab stop. */
      readonly href: string;
      readonly disabled?: undefined;
      readonly role?: undefined;
      readonly tabIndex?: undefined;
    }
  | {
      readonly href?: undefined;
      readonly disabled?: boolean | undefined;
      /**
       * Escape hatch for a proper radiogroup owner — see `RadioChoiceGroup`,
       * the organism that owns an exclusive choice set's roving focus and
       * arrow keys. A link cannot take a radio role, so
       * this is only offered alongside the button form. `aria-pressed` and
       * `role="radio"`/`aria-checked` cannot both describe one control, so
       * setting this swaps the emitted attribute; the owner still supplies
       * `tabIndex` and its own roving-focus and arrow-key handling.
       */
      readonly role?: 'radio' | undefined;
      /** Paired with `role`, for a caller managing roving `tabindex` itself. */
      readonly tabIndex?: number | undefined;
    };

export type ChoiceCardProps = ChoiceCardCore & ChoiceCardTarget;

/**
 * Forwards to the anchor or the button, whichever the card renders —
 * `RadioChoiceGroup` is the caller, moving focus itself for the roving
 * `tabindex` a radiogroup needs.
 */
export const ChoiceCard = forwardRef<HTMLButtonElement | HTMLAnchorElement, ChoiceCardProps>(
  function ChoiceCard(
    {
      label,
      description,
      tag,
      icon,
      selected = false,
      onSelect,
      href,
      ctaLabel,
      accent,
      variant = 'stacked',
      dashed = false,
      selectedStateLabel,
      disabled = false,
      role,
      tabIndex,
    },
    ref,
  ) {
    // Minted here, never taken from the caller: a fixed id collides the second
    // time a page renders a card, and the second card then describes the first.
    // Called before the blank-label return below, unconditionally on every
    // render, because a hook cannot follow one.
    const descriptionId = useId();
    // The card is one focusable element wrapping the label, the description
    // and, on the entry variant, a CTA line and a tag as descendant text, so
    // with no override the accessible name would fold ALL of them in —
    // including the description, announced once as part of the name and a
    // second time as `aria-describedby`'s own target. `aria-labelledby`
    // narrows the name back to everything BUT the description whenever one
    // exists; with none there is nothing else in the card's content to fold
    // in beyond what the implicit wrapper name already carries correctly.
    // An earlier version narrowed to the label alone, which for the entry
    // variant's own conspicuous `ctaLabel` ("Start here →") silently dropped
    // exactly the words a speech-control user would say to target the card.
    const labelId = useId();
    const ctaId = useId();
    const tagId = useId();
    const labelledBy = [
      labelId,
      ctaLabel === undefined ? undefined : ctaId,
      tag === undefined ? undefined : tagId,
    ]
      .filter((id) => id !== undefined)
      .join(' ');

    // An untranslated label leaves a focusable card with no accessible name —
    // worse with a description present, which `aria-labelledby` below would
    // then point at an empty span instead of merely leaving the implicit
    // wrapper name degraded. `Chip` and `RadioChoiceGroup` decide this the
    // same way for the same reason.
    if (label.trim() === '') return null;

    const namedByLabelAlone = description !== undefined;

    const classes = [
      'rp-choice-card',
      `rp-choice-card--${variant}`,
      dashed ? 'rp-choice-card--dashed' : undefined,
    ]
      .filter(Boolean)
      .join(' ');

    // The accent is a persona, not a colour, so what crosses the boundary is a name
    // this component resolves against `tokens.css` itself.
    const accentStyle =
      accent === undefined
        ? undefined
        : ({ '--rp-choice-card-accent': `var(--rp-role-${accent})` } as CSSProperties);

    const body = (
      <>
        {icon === undefined ? null : (
          <IconMedallion decorative selected={selected} size="md" tone="well">
            {icon}
          </IconMedallion>
        )}
        <span className="rp-choice-card__label" {...(namedByLabelAlone ? { id: labelId } : {})}>
          {label}
        </span>
        {description === undefined ? null : (
          <span className="rp-choice-card__description" id={descriptionId}>
            {description}
          </span>
        )}
        {ctaLabel === undefined ? null : (
          <span className="rp-choice-card__cta" id={ctaId}>
            {ctaLabel}
          </span>
        )}
        {tag === undefined ? null : (
          <span className="rp-choice-card__tag" id={tagId}>
            {tag}
          </span>
        )}
        {/* The badge box is always drawn so the card does not change shape, but
          the tick inside it appears only when the card is chosen. Drawing the
          glyph in both states left the fill as the only difference a sighted
          reader could see, which is rule 5 upside down — decoration is not the
          same as a carrier, and this is the carrier. */}
        {variant === 'entry' ? null : (
          <span aria-hidden="true" className="rp-choice-card__check">
            {selected ? '✓' : ''}
          </span>
        )}
        {selected && selectedStateLabel !== undefined ? (
          <VisuallyHidden>{selectedStateLabel}</VisuallyHidden>
        ) : null}
      </>
    );

    if (href !== undefined) {
      // One anchor wrapping the whole card, one tab stop. It navigates, so it
      // stays an anchor: rewriting it as a button would tell a screen-reader user
      // they are staying on the page when they are not.
      return (
        <a
          ref={ref as Ref<HTMLAnchorElement>}
          className={classes}
          href={href}
          style={accentStyle}
          aria-labelledby={namedByLabelAlone ? labelledBy : undefined}
          aria-describedby={description === undefined ? undefined : descriptionId}
        >
          {body}
        </a>
      );
    }

    return (
      <button
        ref={ref as Ref<HTMLButtonElement>}
        className={classes}
        // HTML defaults a button to `submit`; a choice inside the report form
        // would then lodge a half-finished request when pressed.
        type="button"
        style={accentStyle}
        role={role}
        aria-pressed={role === 'radio' ? undefined : selected}
        aria-checked={role === 'radio' ? selected : undefined}
        // `aria-disabled` rather than `disabled`, so the card stays focusable and
        // a keyboard reader can still find out it is there and why it is off.
        aria-disabled={disabled ? true : undefined}
        aria-labelledby={namedByLabelAlone ? labelledBy : undefined}
        aria-describedby={description === undefined ? undefined : descriptionId}
        onClick={disabled ? undefined : onSelect}
        tabIndex={tabIndex}
      >
        {body}
      </button>
    );
  },
);
